
import { supabase } from '@/integrations/supabase/client';
import { BlogPost, AdjacentPosts } from './types';
import { transformDatabasePost } from './transforms';

// Get all blog posts, ordered by publish date
export const getAllBlogPosts = async (): Promise<BlogPost[]> => {
  try {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .order('publish_date', { ascending: false });
    
    if (error) {
      console.error('Error fetching blog posts:', error);
      return [];
    }
    
    return (data || []).map(transformDatabasePost);
  } catch (error) {
    console.error('Error in getAllBlogPosts:', error);
    return [];
  }
};

// Get a single blog post by its slug
export const getBlogPostBySlug = async (slug: string): Promise<BlogPost | null> => {
  try {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('slug', slug)
      .single();
    
    if (error) {
      console.error('Error fetching blog post by slug:', error);
      return null;
    }
    
    return data ? transformDatabasePost(data) : null;
  } catch (error) {
    console.error('Error in getBlogPostBySlug:', error);
    return null;
  }
};

// Get previous and next posts for navigation
export const getAdjacentPosts = async (currentSlug: string): Promise<AdjacentPosts> => {
  try {
    // First get the current post to determine its date
    const currentPost = await getBlogPostBySlug(currentSlug);
    
    if (!currentPost) {
      return { prev: null, next: null };
    }
    
    // Get previous post (published before current)
    const { data: prevData, error: prevError } = await supabase
      .from('blog_posts')
      .select('*')
      .lt('publish_date', currentPost.publish_date)
      .order('publish_date', { ascending: false })
      .limit(1);
    
    // Get next post (published after current)
    const { data: nextData, error: nextError } = await supabase
      .from('blog_posts')
      .select('*')
      .gt('publish_date', currentPost.publish_date)
      .order('publish_date', { ascending: true })
      .limit(1);
    
    if (prevError) console.error('Error fetching previous post:', prevError);
    if (nextError) console.error('Error fetching next post:', nextError);
    
    const prev = prevData && prevData.length > 0 ? transformDatabasePost(prevData[0]) : null;
    const next = nextData && nextData.length > 0 ? transformDatabasePost(nextData[0]) : null;
    
    return { prev, next };
  } catch (error) {
    console.error('Error in getAdjacentPosts:', error);
    return { prev: null, next: null };
  }
};

// Get related blog posts based on matching tags
export const getRelatedBlogPosts = async (currentPostSlug: string, tags: string[], limit = 3): Promise<BlogPost[]> => {
  try {
    if (!tags || tags.length === 0) {
      // If no tags, just get latest posts
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .neq('slug', currentPostSlug)
        .order('publish_date', { ascending: false })
        .limit(limit);
      
      if (error) throw error;
      return (data || []).map(transformDatabasePost);
    }
    
    // Get posts with matching tags
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .neq('slug', currentPostSlug)
      .overlaps('tags', tags)
      .order('publish_date', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    
    // If we didn't get enough related posts, fill with recent posts
    if (data.length < limit) {
      const additionalNeeded = limit - data.length;
      const existingIds = data.map(post => post.id);
      
      const { data: additionalPosts, error: additionalError } = await supabase
        .from('blog_posts')
        .select('*')
        .neq('slug', currentPostSlug)
        .not('id', 'in', `(${existingIds.join(',')})`)
        .order('publish_date', { ascending: false })
        .limit(additionalNeeded);
      
      if (additionalError) throw additionalError;
      
      const transformedData = data.map(transformDatabasePost);
      const transformedAdditional = additionalPosts ? additionalPosts.map(transformDatabasePost) : [];
      
      return [...transformedData, ...transformedAdditional];
    }
    
    return data.map(transformDatabasePost);
  } catch (error) {
    console.error('Error in getRelatedBlogPosts:', error);
    return [];
  }
};

// Search blog posts by query string
export const searchBlogPosts = async (query: string): Promise<BlogPost[]> => {
  try {
    if (!query || query.trim() === '') {
      return await getAllBlogPosts();
    }
    
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .textSearch('tsv', query)
      .order('publish_date', { ascending: false });
    
    if (error) throw error;
    return (data || []).map(transformDatabasePost);
  } catch (error) {
    console.error('Error in searchBlogPosts:', error);
    return [];
  }
};
